
import { useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/features/states";
import { TrustBadge } from "@/components/features/badges";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Package,
  Truck,
  ShieldCheck,
  Star,
  LogOut,
  Mail,
  Phone,
  Globe,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Download,
  Trash2,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { COUNTRIES } from "@/lib/countries";
// AppManagementCard + AboutCard moved to the dedicated SettingsView.

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "ar", label: "العربية" },
  { code: "aa", label: "Qafar" },
  { code: "am", label: "አማርኛ" },
  { code: "so", label: "Soomaali" },
  { code: "om", label: "Afaan Oromoo" },
  { code: "sw", label: "Kiswahili" },
  { code: "ti", label: "ትግርኛ" },
];

function maskPhone(phone: string): string {
  if (!phone) return "—";
  // Show last 4 digits, mask the rest.
  if (phone.length <= 4) return phone;
  return "••••" + phone.slice(-4);
}

export function ProfileView() {
  const { t } = useTranslation();
  const { user, logout, switchRole } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [language, setLanguage] = useState(user?.language || "en");
  const [countryCode, setCountryCode] = useState(user?.countryCode || "+253");
  const [saving, setSaving] = useState(false);

  // Phone verification flow state.
  const [phoneStep, setPhoneStep] = useState<"idle" | "code-sent">("idle");
  const [phoneInput, setPhoneInput] = useState(user?.phone || "");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneDevCode, setPhoneDevCode] = useState<string | null>(null);
  const [phoneLoading, setPhoneLoading] = useState(false);

  // Phase 5: GDPR — delete account + data export.
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    const res = await api.auth.exportData();
    setExporting(false);
    if (!res.ok) return toast.error(res.error || t("profile.exportFailed"));
    try {
      const blob = new Blob([JSON.stringify(res.data!.bundle, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `horndeliver-data-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("profile.dataExported"));
    } catch {
      toast.error(t("profile.downloadFailed"));
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirmEmail.trim()) return toast.error(t("profile.enterEmailConfirm"));
    setDeleting(true);
    const res = await api.auth.deleteAccount({
      confirmEmail: deleteConfirmEmail.trim(),
    });
    setDeleting(false);
    if (!res.ok) return toast.error(res.error || t("profile.erasureFailed"));
    toast.success(t("profile.accountErased"));
    setDeleteOpen(false);
    setDeleteConfirmEmail("");
    // Force reload to clear client state.
    if (typeof window !== "undefined") {
      window.location.hash = "";
      window.location.reload();
    }
  };

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    const res = await api.auth.updateProfile({ name, phone, language, countryCode });
    setSaving(false);
    if (!res.ok) return toast.error(res.error);
    useAuthStore.getState().setUser(res.data!.user);
    toast.success(t("profile.saved"));
  };

  const sendPhoneCode = async () => {
    if (!phoneInput.trim()) return toast.error(t("profile.enterPhone"));
    setPhoneLoading(true);
    const res = await api.auth.sendPhoneOtp({ phone: phoneInput.trim() });
    setPhoneLoading(false);
    if (!res.ok) return toast.error(res.error || t("profile.sendCodeFailed"));
    setPhoneDevCode(res.data?.devCode ?? null);
    setPhoneStep("code-sent");
    toast.success(t("profile.codeSent"));
  };

  const verifyPhone = async () => {
    if (phoneCode.length !== 6) return toast.error(t("profile.enterCode"));
    setPhoneLoading(true);
    const res = await api.auth.verifyPhone({ code: phoneCode });
    setPhoneLoading(false);
    if (!res.ok) return toast.error(res.error || t("profile.verificationFailed"));
    useAuthStore.getState().setUser(res.data!.user);
    setPhoneStep("idle");
    setPhoneCode("");
    setPhoneDevCode(null);
    toast.success(t("profile.phoneVerifiedSuccess"));
  };

  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader title={t("profile.title")} subtitle={t("profile.subtitle")} />

      {/* Profile card */}
      <Card>
        <CardContent className="flex items-center gap-4 p-5">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="bg-primary/10 text-lg font-bold text-primary">
              {user.name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="text-lg font-bold">{user.name}</p>
            <p className="flex items-center gap-1 text-sm text-muted-foreground">
              <Mail className="h-3.5 w-3.5" /> {user.email}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 text-sm">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                {user.rating.toFixed(1)} ({user.ratingCount})
              </span>
              <TrustBadge status={user.trustStatus} />
              {user.emailVerified ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" /> {t("profile.emailVerified")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  <AlertCircle className="h-3 w-3" /> {t("profile.emailUnverified")}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Role switcher */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">{t("profile.yourActiveRole")}</p>
          <p className="text-xs text-muted-foreground">
            {t("profile.roleHintV2")}
          </p>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => switchRole("customer")}
              className={`flex flex-col items-center gap-1.5 rounded-xl border p-4 transition ${
                user.activeRole === "customer" ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <Package className={`h-6 w-6 ${user.activeRole === "customer" ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-sm font-semibold">{t("profile.sender")}</span>
              <span className="text-xs text-muted-foreground">{t("profile.senderHint")}</span>
            </button>
            <button
              onClick={() => switchRole("provider")}
              className={`flex flex-col items-center gap-1.5 rounded-xl border p-4 transition ${
                user.activeRole === "provider" ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <Truck className={`h-6 w-6 ${user.activeRole === "provider" ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-sm font-semibold">{t("profile.provider")}</span>
              <span className="text-xs text-muted-foreground">{t("profile.providerHint")}</span>
            </button>
            <button
              onClick={() => switchRole("driver")}
              className={`flex flex-col items-center gap-1.5 rounded-xl border p-4 transition ${
                user.activeRole === "driver" ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <Truck className={`h-6 w-6 ${user.activeRole === "driver" ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-sm font-semibold">{t("profile.driver")}</span>
              <span className="text-xs text-muted-foreground">{t("profile.courier")}</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Phone verification */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold">{t("profile.phoneVerification")}</p>
            </div>
            {user.phoneVerified ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                <CheckCircle2 className="h-3 w-3" /> {t("profile.phoneVerified")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                <AlertCircle className="h-3 w-3" /> {t("profile.phoneNotVerified")}
              </span>
            )}
          </div>

          {user.phoneVerified ? (
            <p className="text-sm text-muted-foreground">
              {t("profile.phoneVerifiedPrefix")} <span className="font-mono font-medium text-foreground">{maskPhone(user.phone || "")}</span> {t("profile.phoneVerifiedSuffix")}
            </p>
          ) : phoneStep === "idle" ? (
            <div className="space-y-2">
              <Label htmlFor="verify-phone">{t("profile.phoneNumber")}</Label>
              <div className="flex gap-2">
                <select
                  aria-label={t("auth.countryCodeLabel")}
                  className="h-9 w-28 shrink-0 rounded-md border border-input bg-background px-2 text-sm"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  disabled
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <Input
                  id="verify-phone"
                  className="flex-1"
                  placeholder={t("profile.phoneInputPlaceholder", { code: selectedCountry.code })}
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                />
              </div>
              <Button className="w-full" onClick={sendPhoneCode} disabled={phoneLoading}>
                {phoneLoading ? t("profile.sending") : t("profile.sendVerificationCode")}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {phoneDevCode && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-center text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                  <span className="font-semibold">{t("common.demoMode")}:</span> {t("auth.demoCode")}{" "}
                  <span className="font-mono font-bold tracking-widest">{phoneDevCode}</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {t("profile.codeSentToPrefix")} <span className="font-medium text-foreground">{phoneInput}</span>.
              </p>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={phoneCode} onChange={(v) => setPhoneCode(v)}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setPhoneStep("idle");
                    setPhoneCode("");
                    setPhoneDevCode(null);
                  }}
                >
                  {t("common.back")}
                </Button>
                <Button className="flex-1" onClick={verifyPhone} disabled={phoneLoading || phoneCode.length !== 6}>
                  {phoneLoading ? t("profile.verifying") : t("common.verify")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit form */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">{t("profile.editDetails")}</p>
          <div className="space-y-1.5">
            <Label htmlFor="name">{t("profile.fullName")}</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">{t("profile.phone")}</Label>
            <div className="flex gap-2">
              <select
                aria-label={t("auth.countryCodeLabel")}
                className="h-9 w-28 shrink-0 rounded-md border border-input bg-background px-2 text-sm"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="phone"
                  className="pl-9"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("profile.phoneInputPlaceholder", { code: selectedCountry.code })}
                />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lang">{t("profile.language")}</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <select
                id="lang"
                className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>
          <Button className="w-full" onClick={save} disabled={saving}>
            {saving ? t("profile.saving") : t("profile.saveChanges")}
          </Button>
        </CardContent>
      </Card>

      {/* Trust + logout */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate("app/trust")}>
            <ShieldCheck className="mr-2 h-4 w-4" /> {t("profile.idVerification")}
            <span className="ml-auto"><TrustBadge status={user.trustStatus} /></span>
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate("app/notifications")}>
            <MessageSquare className="mr-2 h-4 w-4" /> {t("profile.notifications")}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-destructive" onClick={() => logout()}>
            <LogOut className="mr-2 h-4 w-4" /> {t("profile.signOut")}
          </Button>
        </CardContent>
      </Card>

      {/* ====================================================== */}
      {/* DATA & PRIVACY (GDPR)                                  */}
      {/* ====================================================== */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold">{t("profile.dataPrivacy")}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("profile.dataPrivacyDesc")}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              onClick={handleExportData}
              disabled={exporting}
              className="justify-start"
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {exporting ? t("profile.exporting") : t("profile.exportMyData")}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              className="justify-start border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("profile.deleteAccount")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* App Management + About moved to the dedicated Settings view
          (app/settings) per the V2 navigation redesign. */}

      {/* ====================================================== */}
      {/* DELETE-ACCOUNT CONFIRM DIALOG                          */}
      {/* ====================================================== */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {t("profile.deleteAccountTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("profile.deleteAccountDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-email">{t("profile.emailConfirmation")}</Label>
            <Input
              id="confirm-email"
              type="email"
              placeholder={user.email}
              value={deleteConfirmEmail}
              onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              autoComplete="email"
            />
            {deleteConfirmEmail &&
              deleteConfirmEmail.trim().toLowerCase() !==
                user.email.toLowerCase() && (
                <p className="text-xs text-destructive">
                  {t("profile.emailMismatch")}
                </p>
              )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteConfirmEmail("");
              }}
              disabled={deleting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={
                deleting ||
                deleteConfirmEmail.trim().toLowerCase() !==
                  user.email.toLowerCase()
              }
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("profile.erasing")}
                </>
              ) : (
                <>
                  <Trash2 className="mr-1.5 h-4 w-4" /> {t("profile.eraseMyAccount")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="pb-4 text-center text-xs text-muted-foreground">
        {t("common.appName")} · {t("profile.memberSince", { date: new Date(user.createdAt).toLocaleDateString() })}
      </p>
    </div>
  );
}
